import {Injectable, inject} from '@angular/core';
import {
  NEVER,
  Subject,
  catchError,
  combineLatest,
  mergeMap,
  of,
  throwError,
} from 'rxjs';
import {SOCKET_ROOM} from '../../enums/events-buffer/socket/socket-rooms-enum';
import {SOCKET_SERVICE_EVENT} from '../../enums/events-buffer/socket/socket-service-event.enum';
import {Logger} from '../../interfaces/Logger';
import {IService} from '../../interfaces/service.interface';
import {SocketErrorResponse} from '../../interfaces/socket-error.model';
import {ISocketPayload} from '../../interfaces/socket-payload.interface';
import {SocketRoomHandlerService} from '../../services/socket/socket-room-handler.service';
import {SocketService} from '../../services/socket/socket.service';
import {ServicesService} from './services.service';
import {SignatureService} from './signature.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DestroyRef} from '@angular/core';
import {EVENT_BUS} from '@shared/domain/tokens/event-bus.token';
import {ServiceFinalizedEvent} from '@platform/event-bus/domain/events/service-finalized.event';

@Injectable({
  providedIn: 'root',
})
export class ServicesSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly servicesService = inject(ServicesService);
  private readonly signatureService = inject(SignatureService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed$: Subject<void> = new Subject<void>();
  private readonly eventBus = inject(EVENT_BUS);

  constructor() {
    this.servicesRoomListener();
  }

  clearSubscriptions(): void {
    this.destroyed$.next();
  }

  /**
   * Listens to the service room entry
   *
   * @private
   * @memberof ServicesSocketHandlerService
   */
  private servicesRoomListener(): void {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.service)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.initializeServiceListeners();
      });
  }

  /**
   * Initializes all services room listeners
   *
   * @private
   * @memberof ServicesSocketHandlerService
   */
  private initializeServiceListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.service} 🪩] [Service listeners initialized]`
    );
    this.onServiceFinishedListener();
    this.onServiceStartedListener();
  }

  /**
   * Listens to the socket event that is activated when the service has been terminated
   *
   * @private
   * @memberof ServicesSocketHandlerService
   */
  private onServiceFinishedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.service} 🪩] [Service finished listener initialized]`
    );

    this.socketService
      .receive<ISocketPayload>(SOCKET_SERVICE_EVENT.finished)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const service = eventResponse.payload.service as IService;
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            return combineLatest([of(service), signature$]);
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([service]: [IService, string]) => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.service} 🪩] [Service finished socket recieved]`
        );

        if (service && service.closed) {
          this.servicesService.notifyOnServiceFinished();
          this.eventBus.publish(new ServiceFinalizedEvent());
        }
      });
  }

  /**
   * Listens to the socket event that is fired when a service is started
   *
   * @private
   * @memberof ServicesSocketHandlerService
   */
  private onServiceStartedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.service} 🪩] [Service started listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_SERVICE_EVENT.started)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const service = eventResponse.payload.service as IService;
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            const insertServiceOnDB$ =
              this.servicesService.insertService(service);

            if (service && !service.closed) {
              return combineLatest([insertServiceOnDB$, signature$]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.servicesService.notifyOnServiceStarted();
      });
  }
}
